import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const INSTAGRAM_URL = 'https://www.instagram.com/t.yokota0809/';
const X_URL = 'https://x.com/Yokota5Yokota';
const LINE_URL = 'https://line.me/ti/p/9_7o9hrucX';
const LINE_ID = 'yokota.0809';
const LINE_ACCOUNT_NAME = '馬込古民家 公式';
const TIKTOK_URL = 'https://www.tiktok.com/@rokota0809';
const YOUTUBE_URL = 'https://www.youtube.com/@rokota0809';
const FACEBOOK_URL = 'https://www.facebook.com/share/1FTgxY2mFc/?mibextid=wwXIfr';

type Tab = 'instagram' | 'x' | 'line' | 'tiktok' | 'youtube' | 'facebook';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 px-3 py-1 rounded text-xs font-bold whitespace-nowrap cursor-pointer transition-all"
      style={{
        background: copied ? '#06C755' : 'rgba(6,199,85,0.1)',
        color: copied ? '#fff' : '#06C755',
        border: '1px solid #06C755',
      }}
    >
      <span className="w-4 h-4 flex items-center justify-center">
        <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
      </span>
      {copied ? t('sns.copied') : t('sns.copy')}
    </button>
  );
}

export default function SnsSection() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('instagram');
  const xContainerRef = useRef<HTMLDivElement>(null);
  const igContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeTab !== 'x') return;
    const existing = document.getElementById('twitter-wjs');
    if (!existing) {
      const script = document.createElement('script');
      script.id = 'twitter-wjs';
      script.src = 'https://platform.twitter.com/widgets.js';
      script.async = true;
      script.charset = 'utf-8';
      document.body.appendChild(script);
    } else {
      if ((window as any).twttr && (window as any).twttr.widgets) {
        (window as any).twttr.widgets.load(xContainerRef.current);
      }
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'instagram') return;
    const existing = document.getElementById('instagram-embed-script');
    if (!existing) {
      const script = document.createElement('script');
      script.id = 'instagram-embed-script';
      script.src = 'https://www.instagram.com/embed.js';
      script.async = true;
      document.body.appendChild(script);
    } else {
      if ((window as any).instgrm) {
        (window as any).instgrm.Embeds.process();
      }
    }
  }, [activeTab]);

  return (
    <section
      id="sns"
      className="py-20 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #fdf6e3 0%, #f5e6c8 50%, #ede0c4 100%)',
        fontFamily: "'Noto Serif JP', serif",
      }}
    >
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #8B2500, #c8a96e, #8B2500)' }} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ヘッダー */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-px w-16" style={{ background: '#8B2500' }} />
            <span className="text-xs tracking-widest font-bold" style={{ color: '#8B2500' }}>◆ {t('sns.badge')} ◆</span>
            <div className="h-px w-16" style={{ background: '#8B2500' }} />
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-3" style={{ color: '#3d1f00' }}>
            {t('sns.title')}
          </h2>
          <p className="text-sm" style={{ color: '#6b4c2a' }}>
            {t('sns.subtitle')}
          </p>
        </div>

        {/* Instagram グリッド表示 - 昭和レトロ画像に更新 */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg" style={{ background: 'linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}>
                <i className="ri-instagram-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold" style={{ color: '#3d1f00' }}>{t('sns.instagram.title')}</h3>
                <p className="text-xs" style={{ color: '#9a7a50' }}>{t('sns.instagram.subtitle')}</p>
              </div>
            </div>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #f09433, #dc2743, #bc1888)', color: '#fff', boxShadow: '0 3px 12px rgba(220,39,67,0.35)' }}
            >
              <i className="ri-instagram-line text-base"></i>
              {t('sns.instagram.follow')}
            </a>
          </div>

          {/* Instagram 投稿グリッド - 昭和レトロテイスト画像 */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {[
              { seq: 301, prompt: '1970s Japan traditional kominka house interior with chabudai low table vintage rotary black telephone shoji screens warm nostalgic lighting retro atmosphere film grain texture sepia tone photography' },
              { seq: 302, prompt: '1970s Japanese retro home appliances vintage electric fan old television wooden radio warm interior lighting nostalgic Showa era atmosphere film photography grainy texture' },
              { seq: 303, prompt: '1970s Japan vintage poster on wall traditional tatami room retro Japanese advertisement nostalgic Showa era decor warm lighting film grain sepia photography' },
              { seq: 304, prompt: '1970s Japanese traditional house wooden architecture aged wood beams shoji paper screens soft sunlight through window nostalgic atmosphere vintage film photography warm sepia tone' },
              { seq: 305, prompt: '1970s Japan retro lifestyle scene traditional tea set on chabudai table vintage cushions tatami floor warm nostalgic lighting Showa era atmosphere film grain photography' },
              { seq: 306, prompt: '1970s Japanese kominka house engawa wooden veranda traditional garden view afternoon sunlight nostalgic peaceful atmosphere vintage film photography warm retro aesthetic' },
            ].map((item) => (
              <a
                key={item.seq}
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="relative rounded-lg overflow-hidden cursor-pointer group"
                style={{ paddingBottom: '100%' }}
              >
                <img
                  src={`https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28item.prompt%29%7D&width=400&height=400&seq=${item.seq}&orientation=squarish`}
                  alt="Instagram post"
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-4 text-white">
                    <div className="flex items-center gap-1">
                      <i className="ri-heart-fill text-lg"></i>
                      <span className="text-sm font-bold">{Math.floor(Math.random() * 200) + 50}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-chat-3-fill text-lg"></i>
                      <span className="text-sm font-bold">{Math.floor(Math.random() * 30) + 5}</span>
                    </div>
                  </div>
                </div>
                <div className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: 'rgba(0,0,0,0.6)' }}>
                  <i className="ri-instagram-line text-white text-sm"></i>
                </div>
              </a>
            ))}
          </div>
          <p className="text-center text-xs" style={{ color: '#9a7a50' }}>
            {t('sns.instagram.viewMore')}
          </p>
        </div>

        {/* X (Twitter) タブ */}
        {activeTab === 'x' && (
          <div ref={xContainerRef}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 flex items-center justify-center rounded" style={{ background: '#000' }}>
                <i className="ri-twitter-x-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold" style={{ color: '#3d1f00' }}>X (Twitter) フィード</p>
                <p className="text-xs" style={{ color: '#9a7a50' }}>最新のツイートをご覧ください</p>
              </div>
              <a
                href={X_URL}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
                style={{ background: '#000', color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}
              >
                <i className="ri-twitter-x-line"></i>
                フォローする
              </a>
            </div>
            <div className="rounded-lg p-6 text-center" style={{ background: 'rgba(139,37,0,0.04)', border: '1.5px dashed #c8a96e' }}>
              <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-4" style={{ background: '#000' }}>
                <i className="ri-twitter-x-line text-white text-3xl"></i>
              </div>
              <p className="text-sm font-bold mb-2" style={{ color: '#3d1f00' }}>Xアカウントを設定してください</p>
              <p className="text-xs mb-4 leading-relaxed" style={{ color: '#6b4c2a' }}>
                XのアカウントURLを設定すると、<br />
                こちらに最新ツイートが表示されます。
              </p>
              <a
                href={X_URL}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
                style={{ background: '#000', color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}
              >
                <i className="ri-twitter-x-line"></i>
                Xを見る
              </a>
              <div className="mt-8 space-y-4 text-left">
                {[
                  { text: '【馬込古民家】今日も素敵なゲストをお迎えしました🏮 東京の下町情緒あふれる古民家で、特別なひとときをお過ごしいただけます。ご予約はプロフィールのリンクから！ #馬込古民家 #東京民泊 #古民家ステイ', time: '2時間前', likes: 42, retweets: 12 },
                  { text: '民泊・旅館業の許可申請サポートを行っています。東京都大田区・品川区・目黒区・世田谷区エリアを中心に対応。初回相談無料です！お気軽にお問い合わせください📞 #民泊 #旅館業 #東京', time: '1日前', likes: 87, retweets: 31 },
                  { text: '【お知らせ】2026年版「東京民泊完全ガイド」を無料公開しました！民泊申請の手順から収益化のコツまで詳しく解説しています。ぜひご活用ください📖 #民泊ガイド #Airbnb東京', time: '3日前', likes: 156, retweets: 64 },
                ].map((tweet, i) => (
                  <div
                    key={i}
                    className="rounded-lg p-4 text-left"
                    style={{ background: 'rgba(255,248,230,0.95)', border: '1.5px solid #c8a96e' }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0" style={{ border: '2px solid #c8a96e' }}>
                        <img
                          src="https://readdy.ai/api/search-image?query=traditional%20Japanese%20house%20logo%20icon%20minimalist%20elegant%20dark%20red%20color&width=40&height=40&seq=100&orientation=squarish"
                          alt="avatar"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-bold" style={{ color: '#3d1f00' }}>馬込古民家</p>
                        <p className="text-xs" style={{ color: '#9a7a50' }}>@magome_kominka</p>
                      </div>
                      <div className="ml-auto w-6 h-6 flex items-center justify-center">
                        <i className="ri-twitter-x-line text-sm" style={{ color: '#000' }}></i>
                      </div>
                    </div>
                    <p className="text-sm leading-relaxed mb-3" style={{ color: '#4a3728' }}>{tweet.text}</p>
                    <div className="flex items-center gap-5 pt-2" style={{ borderTop: '1px solid rgba(200,169,110,0.4)' }}>
                      <span className="flex items-center gap-1.5 text-xs" style={{ color: '#9a7a50' }}>
                        <i className="ri-heart-line"></i>
                        {tweet.likes}
                      </span>
                      <span className="flex items-center gap-1.5 text-xs" style={{ color: '#9a7a50' }}>
                        <i className="ri-repeat-line"></i>
                        {tweet.retweets}
                      </span>
                      <span className="text-xs ml-auto" style={{ color: '#9a7a50' }}>{tweet.time}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs mt-4" style={{ color: '#9a7a50' }}>※ サンプル表示です。実際のXアカウントを設定するとリアルタイムの投稿が表示されます。</p>
            </div>
          </div>
        )}

        {/* TikTok タブ */}
        {activeTab === 'tiktok' && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 flex items-center justify-center rounded" style={{ background: '#010101' }}>
                <i className="ri-tiktok-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold" style={{ color: '#3d1f00' }}>TikTok</p>
                <p className="text-xs" style={{ color: '#9a7a50' }}>動画で古民家の魅力をお届けしています</p>
              </div>
              <a
                href={TIKTOK_URL}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
                style={{ background: '#010101', color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}
              >
                <i className="ri-tiktok-line"></i>
                フォローする
              </a>
            </div>
            <div className="rounded-lg p-8 text-center" style={{ background: 'rgba(139,37,0,0.04)', border: '1.5px dashed #c8a96e' }}>
              <div
                className="w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-5"
                style={{ background: '#010101' }}
              >
                <i className="ri-tiktok-line text-white text-4xl"></i>
              </div>
              <h3 className="text-lg font-extrabold mb-2" style={{ color: '#3d1f00' }}>@rokota0809</h3>
              <p className="text-sm leading-relaxed mb-6" style={{ color: '#4a3728' }}>
                古民家の日常・民泊運営の裏側・東京下町の魅力を<br />
                ショート動画でお届けしています。<br />
                ぜひフォローしてチェックしてください！
              </p>
              <ul className="space-y-2.5 mb-7 text-left max-w-xs mx-auto">
                {[
                  '古民家リノベーションの様子',
                  '東京・馬込エリアの観光スポット紹介',
                  '民泊運営のリアルな日常',
                  'ゲストとの思い出ショート動画',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#4a3728' }}>
                    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full mt-0.5" style={{ background: '#010101' }}>
                      <i className="ri-check-line text-white text-xs"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href={TIKTOK_URL}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-7 py-3 rounded text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
                style={{ background: '#010101', color: '#fff', boxShadow: '0 3px 10px rgba(0,0,0,0.3)' }}
              >
                <i className="ri-tiktok-line text-base"></i>
                TikTokを見る
              </a>
              <p className="text-xs mt-4" style={{ color: '#9a7a50' }}>※ サンプル表示です。実際のTikTokアカウントを設定するとリアルタイムの動画が表示されます。</p>
            </div>
          </div>
        )}

        {/* YouTube タブ */}
        {activeTab === 'youtube' && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 flex items-center justify-center rounded" style={{ background: '#FF0000' }}>
                <i className="ri-youtube-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold" style={{ color: '#3d1f00' }}>YouTube チャンネル</p>
                <p className="text-xs" style={{ color: '#6b4c2a' }}>古民家の魅力を動画でお届けしています</p>
              </div>
              <a
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
                style={{ background: '#FF0000', color: '#fff', boxShadow: '0 2px 8px rgba(255,0,0,0.3)' }}
              >
                <i className="ri-youtube-line"></i>
                チャンネル登録
              </a>
            </div>
            <div className="rounded-lg p-8 text-center" style={{ background: 'rgba(139,37,0,0.04)', border: '1.5px dashed #c8a96e' }}>
              <div
                className="w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-5"
                style={{ background: '#FF0000' }}
              >
                <i className="ri-youtube-line text-white text-4xl"></i>
              </div>
              <h3 className="text-lg font-extrabold mb-2" style={{ color: '#3d1f00' }}>@rokota0809</h3>
              <p className="text-sm leading-relaxed mb-6" style={{ color: '#4a3728' }}>
                古民家の日常・民泊運営の裏側・東京下町の魅力を<br />
                YouTube動画でじっくりお届けしています。<br />
                ぜひチャンネル登録してご覧ください！
              </p>
              <ul className="space-y-2.5 mb-7 text-left max-w-xs mx-auto">
                {[
                  '古民家リノベーション・DIYの記録',
                  '民泊申請・運営ノウハウ解説',
                  '東京・馬込エリアの観光スポット紹介',
                  'ゲストとの思い出・宿泊体験レポート',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#4a3728' }}>
                    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full mt-0.5" style={{ background: '#FF0000' }}>
                      <i className="ri-check-line text-white text-xs"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              {/* サムネイルグリッド */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                {[
                  { seq: 201, prompt: 'traditional Japanese kominka house renovation process, workers, wooden beams, warm interior lighting, documentary style photography, cinematic quality' },
                  { seq: 202, prompt: 'Tokyo Magome neighborhood street view, traditional Japanese townscape, cherry blossoms, warm afternoon light, travel vlog style photography' },
                  { seq: 203, prompt: 'Japanese minpaku airbnb room setup, cozy tatami room, soft lighting, travel accommodation, interior photography cinematic style' },
                  { seq: 204, prompt: 'Japanese garden maintenance, raking gravel, pruning bonsai, traditional aesthetic, peaceful atmosphere, documentary photography' },
                  { seq: 205, prompt: 'Japanese kominka wooden architecture detail, aged wood texture, traditional craftsmanship, warm light, close up photography cinematic' },
                  { seq: 206, prompt: 'Tokyo local food market, fresh vegetables, traditional Japanese ingredients, vibrant colors, travel vlog style photography' },
                ].map((item) => (
                  <div
                    key={item.seq}
                    className="relative rounded-lg overflow-hidden cursor-pointer group"
                    style={{ paddingBottom: '56.25%' }}
                  >
                    <img
                      src={`https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28item.prompt%29%7D&width=400&height=225&seq=${item.seq}&orientation=landscape`}
                      alt="YouTube動画サムネイル"
                      className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: '#FF0000' }}>
                        <i className="ri-play-fill text-white text-lg"></i>
                      </div>
                    </div>
                    <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded text-xs font-bold" style={{ background: 'rgba(0,0,0,0.75)', color: '#fff' }}>
                      <i className="ri-youtube-line mr-1"></i>YouTube
                    </div>
                  </div>
                ))}
              </div>
              <a
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-7 py-3 rounded text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
                style={{ background: '#FF0000', color: '#fff', boxShadow: '0 3px 10px rgba(255,0,0,0.35)' }}
              >
                <i className="ri-youtube-line text-base"></i>
                YouTubeチャンネルを見る
              </a>
              <p className="text-xs mt-4" style={{ color: '#9a7a50' }}>※ サンプル表示です。実際のYouTubeチャンネルを設定するとリアルタイムの動画が表示されます。</p>
            </div>
          </div>
        )}

        {/* Facebook タブ */}
        {activeTab === 'facebook' && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 flex items-center justify-center rounded" style={{ background: '#1877F2' }}>
                <i className="ri-facebook-fill text-white text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold" style={{ color: '#3d1f00' }}>Facebook ページ</p>
                <p className="text-xs" style={{ color: '#9a7a50' }}>最新情報・イベントをFacebookでも発信しています</p>
              </div>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
                style={{ background: '#1877F2', color: '#fff', boxShadow: '0 2px 8px rgba(24,119,242,0.3)' }}
              >
                <i className="ri-facebook-fill"></i>
                いいね！する
              </a>
            </div>

            <div className="rounded-lg p-8" style={{ background: 'rgba(139,37,0,0.04)', border: '1.5px dashed #c8a96e' }}>
              <div className="flex flex-col md:flex-row items-center gap-10">

                {/* QRコードエリア */}
                <div className="flex flex-col items-center gap-4 flex-shrink-0">
                  <div
                    className="relative rounded-xl overflow-hidden"
                    style={{
                      width: 200,
                      height: 200,
                      border: '4px solid #1877F2',
                      boxShadow: '0 4px 20px rgba(24,119,242,0.25)',
                    }}
                  >
                    <div className="w-full h-full flex flex-col items-center justify-center" style={{ background: '#fff' }}>
                      <div className="w-32 h-32 flex items-center justify-center mb-2" style={{ background: '#f0fdf4', borderRadius: 8 }}>
                        <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="5" y="5" width="30" height="30" rx="3" fill="#1877F2"/>
                          <rect x="10" y="10" width="20" height="20" rx="2" fill="white"/>
                          <rect x="14" y="14" width="12" height="12" rx="1" fill="#1877F2"/>
                          <rect x="65" y="5" width="30" height="30" rx="3" fill="#1877F2"/>
                          <rect x="70" y="10" width="20" height="20" rx="2" fill="white"/>
                          <rect x="74" y="14" width="12" height="12" rx="1" fill="#1877F2"/>
                          <rect x="82" y="14" width="12" height="12" rx="1" fill="#1877F2"/>
                          <rect x="42" y="42" width="6" height="6" fill="#1877F2"/>
                          <rect x="58" y="42" width="6" height="6" fill="#1877F2"/>
                          <rect x="74" y="42" width="6" height="6" fill="#1877F2"/>
                          <rect x="82" y="42" width="6" height="6" fill="#1877F2"/>
                          <rect x="42" y="50" width="6" height="6" fill="#1877F2"/>
                          <rect x="58" y="50" width="6" height="6" fill="#1877F2"/>
                          <rect x="74" y="50" width="6" height="6" fill="#1877F2"/>
                          <rect x="42" y="58" width="6" height="6" fill="#1877F2"/>
                          <rect x="50" y="58" width="6" height="6" fill="#1877F2"/>
                          <rect x="66" y="58" width="6" height="6" fill="#1877F2"/>
                          <rect x="42" y="66" width="6" height="6" fill="#1877F2"/>
                          <rect x="58" y="66" width="6" height="6" fill="#1877F2"/>
                          <rect x="74" y="66" width="6" height="6" fill="#1877F2"/>
                          <rect x="42" y="74" width="6" height="6" fill="#1877F2"/>
                          <rect x="50" y="74" width="6" height="6" fill="#1877F2"/>
                          <rect x="66" y="74" width="6" height="6" fill="#1877F2"/>
                          <rect x="42" y="82" width="6" height="6" fill="#1877F2"/>
                          <rect x="58" y="82" width="6" height="6" fill="#1877F2"/>
                          <rect x="74" y="82" width="6" height="6" fill="#1877F2"/>
                          <rect x="82" y="82" width="6" height="6" fill="#1877F2"/>
                        </svg>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-facebook-fill text-lg" style={{ color: '#1877F2' }}></i>
                        <span className="text-xs font-bold" style={{ color: '#1877F2' }}>Facebook</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-center font-medium" style={{ color: '#1877F2' }}>
                    📱 カメラで読み取って<br />すぐに友だち追加！
                  </p>
                </div>

                {/* 説明テキストエリア */}
                <div className="flex-1 text-center md:text-left">
                  <div className="flex items-center gap-2 justify-center md:justify-start mb-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: '#1877F2' }}>
                      <i className="ri-facebook-fill text-white text-base"></i>
                    </div>
                    <h3 className="text-lg font-extrabold" style={{ color: '#3d1f00' }}>Facebook公式アカウント</h3>
                  </div>

                  {/* アカウント名・ID表示ボックス */}
                  <div
                    className="rounded-lg p-4 mb-5 flex flex-col gap-3"
                    style={{ background: '#fff', border: '1.5px solid #1877F2' }}
                  >
                    {/* アカウント名 */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <span
                        className="text-xs font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap"
                        style={{ background: 'rgba(24,119,242,0.12)', color: '#1877F2' }}
                      >
                        アカウント名
                      </span>
                      <span className="text-sm font-bold" style={{ color: '#3d1f00' }}>{LINE_ACCOUNT_NAME}</span>
                    </div>
                    {/* LINE ID */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <span
                        className="text-xs font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap"
                        style={{ background: 'rgba(24,119,242,0.12)', color: '#1877F2' }}
                      >
                        LINE ID
                      </span>
                      <span className="text-sm font-mono font-bold flex-1" style={{ color: '#3d1f00' }}>{LINE_ID}</span>
                      <CopyButton text={LINE_ID} />
                    </div>
                    {/* ID検索の説明 */}
                    <div
                      className="flex items-start gap-2 rounded-md px-3 py-2 text-xs leading-relaxed"
                      style={{ background: 'rgba(24,119,242,0.06)', color: '#4a7a5a' }}
                    >
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-search-line text-xs" style={{ color: '#1877F2' }}></i>
                      </span>
                      <span>
                        LINEアプリの「友だち追加」→「ID検索」で
                        <strong className="mx-1" style={{ color: '#1877F2' }}>{LINE_ID}</strong>
                        を検索して追加できます。
                      </span>
                    </div>
                  </div>

                  <p className="text-sm leading-relaxed mb-5" style={{ color: '#4a3728' }}>
                    LINEで友だち追加すると、最新のお知らせや<br />
                    お得なキャンペーン情報をいち早くお届けします。<br />
                    お問い合わせもLINEから気軽にどうぞ！
                  </p>

                  {/* 特典リスト */}
                  <ul className="space-y-2.5 mb-6">
                    {[
                      '空室情報・予約受付をLINEで即対応',
                      '限定クーポン・割引情報をお届け',
                      '民泊・旅館業に関するご相談も受付中',
                      '24時間いつでもメッセージOK',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#4a3728' }}>
                        <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full mt-0.5" style={{ background: '#1877F2' }}>
                          <i className="ri-check-line text-white text-xs"></i>
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                    <a
                      href={LINE_URL}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
                      style={{ background: '#1877F2', color: '#fff', boxShadow: '0 3px 10px rgba(24,119,242,0.3)' }}
                    >
                      <i className="ri-line-fill text-base"></i>
                      友だち追加する
                    </a>
                    <p className="text-xs self-center" style={{ color: '#9a7a50' }}>
                      ※ QRコードをスマホカメラで読み取るか<br className="sm:hidden" />上のボタンからも追加できます
                    </p>
                  </div>
                </div>
              </div>

              {/* 注意書き */}
              <div className="mt-6 pt-4 text-center" style={{ borderTop: '1px solid rgba(6,199,85,0.5)' }}>
                <p className="text-xs" style={{ color: '#9a7a50' }}>
                  ※ QRコードは実際のLINE公式アカウントのものに差し替えてください。LINE公式アカウントのQRコードはLINE Official Account Managerから取得できます。
                </p>
              </div>
            </div>
          </div>
        )}

        {/* SNSフォローCTA */}
        <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-2 px-6 py-3 rounded text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #f09433, #dc2743, #bc1888)', color: '#fff', boxShadow: '0 3px 10px rgba(220,39,67,0.3)' }}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-instagram-line text-base"></i>
            </span>
            Instagramをフォロー
          </a>
          <a
            href={X_URL}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-2 px-6 py-3 rounded text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
            style={{ background: '#000', color: '#fff', boxShadow: '0 3px 10px rgba(0,0,0,0.2)' }}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-twitter-x-line text-base"></i>
            </span>
            Xをフォロー
          </a>
          <a
            href={TIKTOK_URL}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-2 px-6 py-3 rounded text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
            style={{ background: '#010101', color: '#fff', boxShadow: '0 3px 10px rgba(0,0,0,0.25)' }}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-tiktok-line text-base"></i>
            </span>
            TikTokをフォロー
          </a>
          <a
            href={YOUTUBE_URL}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-2 px-6 py-3 rounded text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
            style={{ background: '#FF0000', color: '#fff', boxShadow: '0 3px 10px rgba(255,0,0,0.3)' }}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-youtube-line text-base"></i>
            </span>
            チャンネル登録
          </a>
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-2 px-6 py-3 rounded text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
            style={{ background: '#1877F2', color: '#fff', boxShadow: '0 3px 10px rgba(24,119,242,0.3)' }}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-facebook-fill text-base"></i>
            </span>
            いいね！する
          </a>
          <a
            href={LINE_URL}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-2 px-6 py-3 rounded text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:-translate-y-0.5"
            style={{ background: '#06C755', color: '#fff', boxShadow: '0 3px 10px rgba(6,199,85,0.3)' }}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-line-fill text-base"></i>
            </span>
            LINE友だち追加
          </a>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #c8a96e, #8B2500, #c8a96e)' }} />
    </section>
  );
}
